import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, createBlock as _createBlock } from "vue"
import _imports_0 from '@/assets.app/images/warren_buffet_disaproves_cancellation.png'


const _hoisted_1 = { class: "cancel-form" }
const _hoisted_2 = { class: "cancel-form__group--submit cancel-form__buttons cancel-form__buttons--vertical" }
const _hoisted_3 = {
  key: 0,
  class: "cancel-form__text"
}
const _hoisted_4 = { class: "cancel-form__text--large" }
const _hoisted_5 = { class: "cancel-form__text--highlighted" }
const _hoisted_6 = { class: "cancel-form__text--highlighted" }
const _hoisted_7 = {
  key: 1,
  class: "cancel-form__text"
}
const _hoisted_8 = { class: "cancel-form__text--large" }
const _hoisted_9 = { class: "cancel-form__text--highlighted" }
const _hoisted_10 = { class: "cancel-form__books-text" }
const _hoisted_11 = { class: "popup--books books" }
const _hoisted_12 = {
  key: 2,
  class: "cancel-form__text cancel-form__text--disclaimer"
}
const _hoisted_13 = {
  key: 3,
  class: "cancel-form__text cancel-form__text--disclaimer"
}
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { key: 1 }
const _hoisted_16 = {
  key: 4,
  class: "cancel-form__error"
}
const _hoisted_17 = { class: "cancel-form" }
const _hoisted_18 = { class: "popup--books cancel-form__group--submit cancel-form__buttons" }

import { computed, onMounted, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRouter } from 'vue-router'

import { BillingView } from '@/models/billing'

import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import BasePopup from '@/components/ui/BasePopup.vue'
import BookCover from '@/app/components/book/card/BookCover.vue'

import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Book } from '@/models/interfaces'
import { Mixpanel } from '@/analytics/mixpanel'


export default /*@__PURE__*/_defineComponent({
  __name: 'CancelPopup',
  props: {
    billing: {}
  },
  emits: ['canceled'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit
const router = useRouter()
const wait = useWait()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)

// For two-step cancellation flow, starting step is 1
const step = ref(1)
const books = ref<Book[]>([])
const cancelDiscountRegistered = ref<boolean | null>(null)
const error = ref('')

onMounted(async () => {
  books.value = await backend.getLatestPopularBooks()
})

const cancelDiscountPricing = ref({
  monthlyBase: '$24/month',
  yearlyBase: '$197',
  zeroPrice: '$0.00',
  yearlyDiscounted: '$147.75',
  monthlyDiscounted: '$12',
})

/*
 * Set the popup title depending on the current step.
 *
 * Step 1: The first step in the two-step cancellation flow.
 * Step 2: The second step in the two-step cancellation flow.
 */
const popupTitle = computed(() => {
  switch (step.value) {
    case 1:
      return 'Are you sure you want to end your subscription?'
    case 2:
      if (!showCancelDiscount.value) {
        return 'Before you go, check out our recent hit summaries.'
      }

      if (showAnnualDiscount.value) {
        return 'Still not sure? Pay now and get the <u>first 3 months free!</u>'
      }

      return 'Still not sure? Try 1 month for <u>50% off</u>!'
  }
  return 'Cancel your subscirption?'
})

/*
 * Set the popup class depending on the current step.
 *
 * For the 1st step we use 'popup--simple',
 * for the 2nd step we need to use a different class
 * because the style of the popup is different.
 */
const popupClass = computed(() => {
  if (step.value === 2 && showCancelDiscount.value) {
    return 'popup--books popup--15449'
  }
  if (step.value === 2) {
    return 'popup--books'
  }
  return 'popup--simple'
})

async function clickKeep(event: Event): Promise<void> {
  event.preventDefault()

  if (showCancelDiscount.value) {
    try {
      event.preventDefault()
      error.value = ''
      wait.start('Applying Discount')
      await props.billing.applyCancelDiscount()
      hide()
      // We need to reload the page as we need to read the local storage again
      window.location.reload()
      wait.end('Applying Discount')
    } catch (err) {
      wait.end('Applying Discount')
      Sentry.captureException(err)
      error.value = 'Failed to apply discount'
    }
  } else {
    hide()
  }
}

function show(): void {
  popup.value?.show()
}

function closeEvent(): void {
  Mixpanel.trackCancelSubscriptionStep(step.value, 'Close Window')
}

function hide(sendEvent: boolean = true): void {
  if (sendEvent) {
    Mixpanel.trackCancelSubscriptionStep(step.value, 'Keep Subscription')
  }
  popup.value?.hide(false)
  step.value = 1
}

/**
 * Returns true if the cancel discount is registered and the user is on a trial.
 *
 * @returns {boolean} True if the cancel discount is registered and the user is on a trial, false otherwise.
 */
const showCancelDiscount = computed(() => {
  return cancelDiscountRegistered.value === true && props.billing.is_trial
})

/**
 * Returns true if the user is on a monthly plan.
 *
 * @returns {boolean} True if the user is on a monthly plan, false otherwise.
 */
const showMonthlyDiscountText = computed(() => {
  return props.billing.plan_id === 'plan_monthly'
})

/**
 * Returns true if the cancel discount is registered and the user is on an annual plan.
 *
 * @returns {boolean} True if the cancel discount is registered and the user is on an annual plan, false otherwise.
 */
const showAnnualDiscount = computed(() => {
  return (
    cancelDiscountRegistered.value === true &&
    props.billing.is_trial &&
    props.billing.plan_id === 'plan_annual'
  )
})

/**
 * Returns the text for the CTA button.
 *
 * @returns {string} The text for the CTA button.
 */
const ctaText = computed(() => {
  if (!(cancelDiscountRegistered.value === true) || !props.billing.is_trial) {
    return 'Keep my membership'
  }

  if (showAnnualDiscount.value) {
    return 'Pay now, get the first 3 months free'
  }

  return 'Get 50% off'
})

async function clickEnd(event: Event): Promise<void> {
  wait.start('Loading')
  event.preventDefault()
  Mixpanel.trackCancelSubscriptionStep(step.value, 'End Subscription')

  cancelDiscountRegistered.value = await backend.registerCancelDiscount()
  step.value += 1
  wait.end('Loading')
}

function openBook(book: any): void {
  Mixpanel.trackCancelSubscriptionStep(step.value, 'Open Book', {
    book_title: book.title,
  })

  if (cancelDiscountRegistered.value === true) {
    return
  }

  router.push({
    name: 'book.page',
    params: {
      url_slug: book.url_slug,
      position: book.position,
    },
  })
}

async function cancelSubscription(event: Event): Promise<void> {
  try {
    event.preventDefault()
    wait.start('Canceling')
    await props.billing.subscribeTo(props.billing.freePlan)
    emit('canceled')
    wait.end('Canceling')
  } catch (error) {
    wait.end('Canceling')
    Sentry.captureException(error)
    alert('Failed to cancel the subscription')
  }
  Mixpanel.trackCancelSubscriptionStep(step.value, 'End Membership')
  hide(false)
}

__expose({
  show,
  hide,
})

return (_ctx: any,_cache: any) => {
  const _component_v_wait = _resolveComponent("v-wait")!
  const _directive_wait = _resolveDirective("wait")!

  return (_openBlock(), _createBlock(BasePopup, {
    ref_key: "popup",
    ref: popup,
    title: popupTitle.value,
    class: _normalizeClass(popupClass.value),
    closeEvent: closeEvent,
    onHide: _cache[1] || (_cache[1] = ($event: any) => (step.value = 1))
  }, {
    default: _withCtx(() => [
      _withDirectives(_createElementVNode("div", null, [
        _cache[4] || (_cache[4] = _createElementVNode("p", null, " Shortform helps you learn more in less time. Do you value your personal growth at more than $1 per hour? If so, keeping Shortform might be worth it. ", -1)),
        _cache[5] || (_cache[5] = _createElementVNode("p", { class: "popup__container" }, [
          _createElementVNode("img", {
            class: "popup__container--image",
            src: _imports_0
          })
        ], -1)),
        _cache[6] || (_cache[6] = _createElementVNode("p", null, [
          _createTextVNode(" We're inspired by Warren Buffett, who said: "),
          _createElementVNode("strong", null, " \"The best investment you can make is an investment in yourself. The more you learn, the more you'll earn.\" ")
        ], -1)),
        _createElementVNode("form", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("button", {
              class: "cancel-form__close-btn btns btns--largest btns--font-large btns--full",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (hide(true)))
            }, " Keep my personal growth "),
            _createElementVNode("button", {
              class: "cancel-form__btn btns btns--largest btns--font-large btns--white btns--full",
              onClick: clickEnd
            }, [
              _createVNode(_component_v_wait, { for: "Loading" }, {
                waiting: _withCtx(() => [
                  _cache[2] || (_cache[2] = _createTextVNode(" Loading ", -1)),
                  _createVNode(ButtonSpinner)
                ]),
                default: _withCtx(() => [
                  _cache[3] || (_cache[3] = _createElementVNode("span", null, "End my personal growth", -1))
                ]),
                _: 1
              })
            ])
          ])
        ])
      ], 512), [
        [_vShow, step.value === 1]
      ]),
      _withDirectives(_createElementVNode("div", null, [
        (showAnnualDiscount.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("p", _hoisted_4, [
                _cache[8] || (_cache[8] = _createTextVNode(" You’ll pay ", -1)),
                _createElementVNode("s", null, _toDisplayString(cancelDiscountPricing.value.yearlyBase), 1),
                _createElementVNode("strong", _hoisted_5, [
                  _cache[7] || (_cache[7] = _createElementVNode("span", { class: "cancel-form__text--small" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(cancelDiscountPricing.value.yearlyDiscounted), 1)
                ]),
                _cache[9] || (_cache[9] = _createTextVNode(" for the first year ", -1)),
                _cache[10] || (_cache[10] = _createElementVNode("strong", null, "charged today", -1)),
                _cache[11] || (_cache[11] = _createTextVNode(". ", -1)),
                _createElementVNode("span", _hoisted_6, _toDisplayString(cancelDiscountPricing.value.zeroPrice), 1),
                _cache[12] || (_cache[12] = _createTextVNode(" for the first three months, then $16.42/mo for the remaining nine months. ", -1))
              ])
            ]))
          : (showCancelDiscount.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("p", _hoisted_8, [
                  _createElementVNode("s", null, _toDisplayString(cancelDiscountPricing.value.monthlyBase), 1),
                  _createElementVNode("strong", _hoisted_9, [
                    _cache[13] || (_cache[13] = _createElementVNode("span", { class: "cancel-form__text--small" }, null, -1)),
                    _createTextVNode(" " + _toDisplayString(cancelDiscountPricing.value.monthlyDiscounted), 1)
                  ]),
                  _cache[14] || (_cache[14] = _createTextVNode(" for the first month, then $24/month after ", -1))
                ])
              ]))
            : _createCommentVNode("", true),
        _createElementVNode("p", _hoisted_10, " We publish 5+ book summaries every week, including these recent hits. " + _toDisplayString(showCancelDiscount.value ? '' : 'Click on any to read.'), 1),
        _createElementVNode("div", _hoisted_11, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(books.value, (book, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: book.id,
              class: _normalizeClass(["popup--books books--container", 'container__item--' + index])
            }, [
              _createVNode(BookCover, {
                doc: book,
                class: "popup--books books--img",
                onClick: ($event: any) => (openBook(book))
              }, null, 8, ["doc", "onClick"])
            ], 2))
          }), 128))
        ]),
        (showAnnualDiscount.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_12, [
              _createTextVNode(" Accepting the discount automatically ends the free trial period. Immediately after you select this discount, you will be charged " + _toDisplayString(cancelDiscountPricing.value.yearlyDiscounted) + " for 1 year of Shortform membership. ", 1),
              _cache[15] || (_cache[15] = _createElementVNode("strong", null, "You can still cancel your membership before your second payment.", -1))
            ]))
          : (showCancelDiscount.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_13, [
                _cache[17] || (_cache[17] = _createTextVNode(" Accepting the discount automatically ends the free trial period. ", -1)),
                (showMonthlyDiscountText.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_14, " You will be charged " + _toDisplayString(cancelDiscountPricing.value.monthlyDiscounted) + " your first month, then charged " + _toDisplayString(cancelDiscountPricing.value.monthlyBase) + " starting month 2. You can change to an annual plan at any time for a prorated cost.", 1))
                  : (_openBlock(), _createElementBlock("span", _hoisted_15, [
                      _cache[16] || (_cache[16] = _createElementVNode("strong", null, "You will be switched to a monthly plan", -1)),
                      _createTextVNode(" and charged " + _toDisplayString(cancelDiscountPricing.value.monthlyDiscounted) + " your first month, then charged " + _toDisplayString(cancelDiscountPricing.value.monthlyBase) + " starting month 2. You can still change back to an annual plan at any time for a prorated cost.", 1)
                    ])),
                _cache[18] || (_cache[18] = _createElementVNode("strong", null, " You can still cancel your membership before your second payment.", -1))
              ]))
            : _createCommentVNode("", true),
        (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(error.value), 1))
          : _createCommentVNode("", true),
        _createElementVNode("form", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("button", {
              class: "cancel-form__close-btn btns btns--largest btns--font-large btns--full",
              onClick: clickKeep
            }, [
              _createVNode(_component_v_wait, { for: "Applying Discount" }, {
                waiting: _withCtx(() => [
                  _cache[19] || (_cache[19] = _createTextVNode(" Applying Discount ", -1)),
                  _createVNode(ButtonSpinner)
                ]),
                default: _withCtx(() => [
                  _createElementVNode("span", null, _toDisplayString(ctaText.value), 1)
                ]),
                _: 1
              })
            ]),
            _withDirectives((_openBlock(), _createElementBlock("button", {
              class: "cancel-form__btn btns btns--largest btns--font-large btns--white btns--full",
              onClick: cancelSubscription
            }, [
              _createVNode(_component_v_wait, { for: "Canceling" }, {
                waiting: _withCtx(() => [
                  _cache[20] || (_cache[20] = _createTextVNode(" Ending ", -1)),
                  _createVNode(ButtonSpinner)
                ]),
                default: _withCtx(() => [
                  _cache[21] || (_cache[21] = _createElementVNode("span", null, "End my membership", -1))
                ]),
                _: 1
              })
            ])), [
              [_directive_wait, 'Canceling', "disabled"]
            ])
          ])
        ])
      ], 512), [
        [_vShow, step.value === 2]
      ])
    ]),
    _: 1
  }, 8, ["title", "class"]))
}
}

})